import { forwardRef, memo } from 'react';
import type Konva from 'konva';
import { Group, Rect, Text } from 'react-konva';
import type { Node } from '../../../core/models/scene';
import { useTranslation } from '../../../hooks/useTranslation';
import { MEDIA_CANVAS_TEST_IDS } from '../../media/constants/mediaCanvasTestIds';

type KonvaNodeEvent = Konva.KonvaEventObject<MouseEvent | TouchEvent | DragEvent | Event>;

interface PdfShapeNodeProps {
  node: Node;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: KonvaNodeEvent) => void;
  onDoubleClick?: (e: KonvaNodeEvent) => void;
  onTransformStart?: (e: KonvaNodeEvent) => void;
  onTransformEnd?: (e: KonvaNodeEvent) => void;
  canWrite?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

const BasePdfShapeNode = forwardRef<Konva.Group, PdfShapeNodeProps>(
  ({ node, onDragStart, onDragMove, onDragEnd, onClick, onDoubleClick, onTransformStart, onTransformEnd, canWrite = true }, ref) => {
    const { t } = useTranslation('diagram');
    const width = node.width ?? node.w ?? 180;
    const height = node.height ?? node.h ?? 96;
    const attrs = isRecord(node.attrs) ? node.attrs : {};
    const rotation = getFiniteNumber(Reflect.get(node, 'rotation'), getFiniteNumber(attrs.rotation, 0));
    const uploadState = attrs.uploadState === 'uploading' ? 'uploading' : 'ready';
    const uploadErrorMessage = typeof attrs.uploadErrorMessage === 'string' ? attrs.uploadErrorMessage : '';
    const name = typeof attrs.name === 'string' && attrs.name.trim().length > 0
      ? attrs.name.trim()
      : t('mediaUpload.pdfShapeStatus.untitledName', { defaultValue: 'Untitled PDF' });

    const statusText =
      uploadState === 'uploading'
        ? t('mediaUpload.pdfShapeStatus.uploading', { defaultValue: 'Uploading PDF...' })
        : uploadErrorMessage.length > 0
          ? uploadErrorMessage
          : '';

    return (
      <Group
        ref={ref}
        id={node.id}
        name="shape"
        data-testid={MEDIA_CANVAS_TEST_IDS.pdfNode}
        data-media-upload-state={uploadErrorMessage.length > 0 ? 'failed' : uploadState}
        x={node.x || 0}
        y={node.y || 0}
        rotation={rotation}
        draggable={canWrite}
        onClick={onClick}
        onDblClick={onDoubleClick}
        onDragStart={(e) => onDragStart?.({ x: e.target.x(), y: e.target.y() })}
        onDragMove={(e) => onDragMove?.({ x: e.target.x(), y: e.target.y() })}
        onDragEnd={(e) => onDragEnd?.({ x: e.target.x(), y: e.target.y() })}
        onTransformStart={onTransformStart}
        onTransformEnd={onTransformEnd}
      >
        <Rect
          x={0}
          y={0}
          width={width}
          height={height}
          cornerRadius={10}
          fill="#FFFFFF"
          stroke="#E2E8F0"
          strokeWidth={1}
        />
        <Rect
          x={12}
          y={12}
          width={40}
          height={48}
          cornerRadius={8}
          fill="#DC2626"
          stroke="#B91C1C"
          strokeWidth={1}
        />
        <Text
          x={12}
          y={28}
          width={40}
          text="PDF"
          align="center"
          fontStyle="bold"
          fontSize={11}
          fill="#FFFFFF"
          listening={false}
        />
        <Text
          x={60}
          y={16}
          width={Math.max(0, width - 72)}
          text={name}
          fontSize={14}
          fontStyle="bold"
          fill="#0F172A"
          ellipsis
          listening={false}
        />
        {statusText ? (
          <Text
            data-testid={
              uploadState === 'uploading'
                ? MEDIA_CANVAS_TEST_IDS.placeholder
                : MEDIA_CANVAS_TEST_IDS.uploadError
            }
            x={12}
            y={height - 20}
            width={Math.max(0, width - 24)}
            text={statusText}
            fontSize={11}
            fill={uploadState === 'uploading' ? '#64748B' : '#DC2626'}
            listening={false}
            ellipsis
          />
        ) : null}
      </Group>
    );
  },
);

BasePdfShapeNode.displayName = 'PdfShapeNode';

export const PdfShapeNode = memo(BasePdfShapeNode);
